// Audit entry detail page — human-readable actor and action labels.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  EntityDetailLayout,
  Icon,
  Spinner,
} from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import { auditActorLabel, humanizeAuditCode } from '../lib/auditLabels.js';
import { resourceTypeLabel } from '../lib/humanLabels.js';

interface AuditRow {
  id: string;
  tenant_id: string | null;
  tenant_name?: string | null;
  actor_user_id: string | null;
  actor_name?: string | null;
  actor_session_id: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  before: unknown;
  after: unknown;
  request_id: string | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
}

export function AuditEntryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<AuditRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setEntry(null);
    setError(null);
    // UX-026: this used to fetch the last 200 audit rows and search them
    // client-side, so every permalink decayed — an entry older than that
    // window stopped resolving, and "Copy permalink" handed out a link that
    // would break. Low-frequency events (a lockdown, say) are both the most
    // likely to fall outside the window and the most worth linking to.
    apiClient
      .get<{ data: AuditRow }>(`/api/v1/audit/${encodeURIComponent(id)}`)
      .then((res) => {
        if (cancelled) return;
        setEntry(res.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setError('That audit entry does not exist, or is outside your tenant.');
          return;
        }
        setError(err instanceof ApiError ? err.message : 'Failed to load audit entry');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/activity')}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <Icon name="arrow_back" /> Activity
        </button>
        <div className="p-6 bg-error-container text-on-error-container rounded-lg" role="alert">
          {error}
        </div>
      </div>
    );
  }
  if (!entry) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  const safeActions = (
    <Button
      variant="secondary"
      onClick={() => {
        void (async () => {
          try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            // ignore
          }
        })();
      }}
    >
      <Icon name="link" className="text-sm" />
      <span className="ml-1">{copied ? 'Copied' : 'Copy permalink'}</span>
    </Button>
  );

  const tenantLabel = entry.tenant_name?.trim() || entry.tenant_id || '— (master)';
  const hasBefore = hasMeaningfulAuditValue(entry.before);
  const hasAfter = hasMeaningfulAuditValue(entry.after);

  return (
    <EntityDetailLayout
      title={humanizeAuditCode(entry.action)}
      subtitle={new Date(entry.created_at).toLocaleString()}
      meta={resourceTypeLabel(entry.resource_type)}
      backHref="/activity"
      backLabel="Activity"
      safeActions={safeActions}
    >
      <section
        aria-label="Actor"
        className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-surface-container-lowest border border-outline-variant rounded-xl p-6"
      >
        <KV k="What happened" v={humanizeAuditCode(entry.action)} />
        <KV k="Performed at" v={new Date(entry.created_at).toLocaleString()} />
        <KV k="Performed by" v={auditActorLabel(entry)} />
        <KV k="Tenant" v={tenantLabel} />
        <KV k="Resource" v={resourceTypeLabel(entry.resource_type)} />
        <KV k="IP" v={entry.ip ?? '—'} />
        <KV k="Device" v={deviceLabel(entry.user_agent)} />
      </section>

      {hasBefore || hasAfter ? (
        <section
          aria-label="Before / after"
          className="grid grid-cols-1 lg:grid-cols-2 gap-4"
        >
          <AuditStateCard title="Before" value={entry.before} empty="No prior state recorded." />
          <AuditStateCard title="After" value={entry.after} empty="No resulting state recorded." />
        </section>
      ) : (
        <section
          aria-label="Before / after"
          className="bg-surface-container-lowest border border-outline-variant rounded-xl p-6"
        >
          <h2 className="font-label-md text-label-md uppercase text-on-surface-variant">
            Before / after
          </h2>
          <p className="mt-2 font-body-md text-body-md text-on-surface-variant">
            No before or after details were recorded for this action.
          </p>
        </section>
      )}
    </EntityDetailLayout>
  );
}

function hasMeaningfulAuditValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

function deviceLabel(userAgent: string | null): string {
  if (!userAgent?.trim()) return '—';
  const value = userAgent.toLowerCase();
  const platform = value.includes('iphone')
    ? 'iPhone'
    : value.includes('ipad')
      ? 'iPad'
      : value.includes('android')
        ? 'Android'
        : value.includes('mac os')
          ? 'Mac'
          : value.includes('windows')
            ? 'Windows'
            : value.includes('linux')
              ? 'Linux'
              : 'Web';
  const browser = value.includes('edg/')
    ? 'Edge'
    : value.includes('chrome/')
      ? 'Chrome'
      : value.includes('safari/')
        ? 'Safari'
        : value.includes('firefox/')
          ? 'Firefox'
          : 'Browser';
  return `${platform} · ${browser}`;
}

function AuditStateCard({
  title,
  value,
  empty,
}: {
  title: string;
  value: unknown;
  empty: string;
}) {
  return (
    <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-6 space-y-3">
      <h2 className="font-label-md text-label-md uppercase text-on-surface-variant">{title}</h2>
      {hasMeaningfulAuditValue(value) ? (
        <pre className="font-code text-code bg-surface-container rounded p-3 overflow-x-auto whitespace-pre-wrap">
          {JSON.stringify(value, null, 2)}
        </pre>
      ) : (
        <p className="font-body-md text-body-md text-on-surface-variant">{empty}</p>
      )}
    </div>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <div className="font-label-md text-label-md uppercase text-on-surface-variant mb-1">{k}</div>
      <div className="font-body-md text-body-md text-on-surface">{v}</div>
    </div>
  );
}
